import api from "@/api";

import deepClone from "@/utils/deep-clone";

const state = {
    list: [],
    details: {},
    detailsParticipant: {},
    accessDenied: false,
    redirect: null,
};

const getters = {
    list(state) {
        return deepClone(state.list || []);
    },
    accessDenied(state) {
        return state.accessDenied;
    },
    details(state) {
        return id => deepClone(state.details[id] || {});
    },
    detailsParticipant(state) {
        return id => deepClone(state.detailsParticipant[id] || {});
    },
    redirect(state) {
        return state.redirect;
    },
};

const actions = {
    getList(
        { commit, state },
        { payload: { id, fullname, localTeam, manager, status, page, cache = false } },
    ) {
        if (cache) {
            const data = getters.list(state);
            if (Array.isArray(data) && data.length) {
                return Promise.resolve();
            }
        }

        return api
            .post("/api/reviewcycles/get/participants", {
                reviewCycle: id,
                fullname: fullname,
                localTeam: localTeam,
                manager: manager,
                status: status,
                page: page,
            })
            .then(res => {
                const list = res.data || [];
                commit("updateList", list);

                list.forEach(participant => {
                    commit("updateDetailsParticipant", { participant });
                });
            });
    },
    getStatus(
        { commit, rootGetters },
        { payload: { reviewCycleId, revieweeId, role, contributorId, viewAs, view, entrypoint } },
    ) {
        commit("updateAccessDenied", false);
        commit("updateRedirect", null);

        // abort if reviewee is not provided

        if (!revieweeId || revieweeId === "null") {
            commit("updateDetails", {
                reviewCycle: {
                    _id: reviewCycleId,
                    status: {},
                    revieweeUser: {},
                    contributorDetails: {},
                    contributors: {},
                    questions: { skills: {} },
                },
            });

            return Promise.resolve();
        }

        // Get current user information
        const user = rootGetters["users/current/user"];

        // get reviewCycle from backend

        return api
            .get(
                `/api/reviewcycles/get/details/${reviewCycleId}/reviewee/${revieweeId}/${role}/${contributorId ||
                    user._id}/viewAs/${viewAs}`,
            )
            .then(res => {
                var data = res.data;

                // show error if user doesn't have access

                if (data.accessDenied) {
                    commit("updateAccessDenied", true);
                    return;
                } else if (data.redirect) {
                    commit("updateRedirect", {
                        revieweeId,
                        reviewCycleId,
                        role: data.redirect,
                        viewAs: data.redirect,
                        contributorId: user._id,
                        view,
                        entrypoint,
                    });
                    return;
                }

                // feed different factories with input from API call

                let details = data;

                // set manager stats

                if (data.statsManager) {
                    details.statsManager = data.statsManager;
                } else {
                    details.statsmanager = {};
                }

                // set feedbackDefaults based on role

                if (viewAs !== "owner") {
                    // not necessary / available for owner

                    details.reviewCycle = data._id;
                    details.receiver = revieweeId;
                    details.feedbackSet = data.contributorDetails.feedbackSet;

                    if (viewAs === "reviewee") {
                        details.type = "reviewReviewee";
                    } else if (viewAs === "peer") {
                        details.type = "reviewPeer";
                    } else if (viewAs === "manager") {
                        details.type = "reviewManager";
                    } else if (viewAs === "additionalManager") {
                        details.type = "reviewAdditionalManager";
                    } else if (viewAs === "directReport") {
                        details.type = "reviewDirectReport";
                    }
                }

                let existingFeedbackSignals;

                if (viewAs === role) {
                    existingFeedbackSignals = data.contributorDetails.answers;
                } else if (data?.contributors?.all?.length) {
                    existingFeedbackSignals = data.contributors.all[0].answers;
                } else {
                    existingFeedbackSignals = {};
                }

                // create feedbackSignal array to store intermediate results

                details.feedbackSignalIndex = 1;
                details.feedbackSignals = [];

                details.feedbackSignals.push({
                    feedbackSignalIndex: 0,
                    content: "",
                    amount: -100,
                });

                // populate with existing values

                data.questions.skills.forEach(function(skill) {
                    // set amount

                    var amount;

                    if (
                        existingFeedbackSignals[skill._id] &&
                        typeof existingFeedbackSignals[skill._id].amount != "undefined"
                    ) {
                        amount = existingFeedbackSignals[skill._id].amount;
                    } else {
                        amount = -100;
                    }

                    // set content

                    var content;

                    if (existingFeedbackSignals[skill._id]) {
                        content = existingFeedbackSignals[skill._id].content;
                    } else {
                        content = "";
                    }

                    // set multipleChoiceOptions

                    var multipleChoiceOptions;

                    if (
                        existingFeedbackSignals[skill._id] &&
                        existingFeedbackSignals[skill._id].answers &&
                        skill.multipleChoiceOptions
                    ) {
                        multipleChoiceOptions = skill.multipleChoiceOptions.map(function(o) {
                            if (
                                existingFeedbackSignals[skill._id].answers.some(function(a) {
                                    return a === o._id;
                                })
                            ) {
                                o.selected = true;
                            } else {
                                o.selected = false;
                            }

                            return o;
                        });
                    } else {
                        multipleChoiceOptions = skill.multipleChoiceOptions;
                    }

                    var feedbackSignal = {
                        feedbackSignalIndex: details.feedbackSignalIndex,
                        content: content,
                        amount: amount,
                        weight: skill.weight,
                        private: skill.private,
                        answerType: skill.answerType,
                        multipleChoiceOptions: multipleChoiceOptions,
                        allowMultipleAnswers: skill.allowMultipleAnswers,
                        label: skill.label,
                        status: skill.status,
                        keyResults: skill.keyResults,
                        skill: skill._id,
                        skillname: skill.name,
                        skilltype: skill.type,
                        skilldescription: skill.description,
                        skillcategory: skill.category,
                        progress: skill.progress,
                        ratingRequirement: skill.ratingRequirement,
                        commentRequirement: skill.commentRequirement,
                        type: details.type,
                        applicableToRole: skill.applicableToRole,
                        inputRequired: skill.inputRequired,
                        role: skill.role ? skill.role : "all",
                        customRole: skill.customRole,
                        descriptionCustomRoles: skill.descriptionCustomRoles,
                        isLastQuestion: skill.isLastQuestion,
                    };
                    if (skill.scale) {
                        // this mean this question has it own scale setting.
                        feedbackSignal.scale = skill.scale; // bad data structure.
                        feedbackSignal.fScale = skill.fScale; // bad data structure.
                    } else {
                        // otherwise we use a global one
                        feedbackSignal.scale = details.scale;
                        feedbackSignal.fScale = details.fScale;
                    }

                    details.feedbackSignals.push(feedbackSignal);

                    details.feedbackSignalIndex++;
                });

                // reset feedbackSignalIndex (for display)

                details.feedbackSignalIndex = 1;

                commit("updateDetails", { reviewCycle: details });
            });
    },

    updateStatus(
        { commit, state },
        {
            payload: {
                reviewCycleId,
                contributorRole,
                reviewee,
                contributorStatus,
                visibility,
                signature = false,
                notes,
                ratingStats,
                peerDeclineMessage,
            },
        },
    ) {
        // set API call input

        const reviewCycle = getters.details(state)(reviewCycleId);

        var data = {
            reviewCycle: reviewCycleId,
            reviewee: reviewee,
            contributorRole: contributorRole,
            contributorStatus: contributorStatus,
            signature: signature,
            notes: notes,
            ratingStats: ratingStats,
            visibility: visibility,
            peerDeclineMessage: peerDeclineMessage,
        };

        // call API

        return api.post("/api/reviewcycles/update/participantstatus", data).then(() => {
            reviewCycle.contributorDetails.status = contributorStatus;
            reviewCycle.contributorDetails.visibility = visibility;

            commit("updateDetails", { reviewCycle });
        });
    },

    selfnominate(_, { payload: { reviewCycle, reviewee } }) {
        return api.post("/api/reviewcycles/update/peers/selfnominate", { reviewCycle, reviewee });
    },

    updatePeers(
        _,
        {
            payload: {
                reviewCycle,
                managerReviewsEnabled,
                peerApprovalEnabled,
                peers,
                message,
                reviewee,
                role,
            },
        },
    ) {
        const data = {
            reviewCycle,
            managerReviewsEnabled,
            peerApprovalEnabled,
            peers,
            message,
            reviewee,
            role,
        };

        return api.post("/api/reviewcycles/update/peers", data);
    },

    updateAccessHolders({ commit, state }, { payload: { reviewCycle, reviewee, accessHolders } }) {
        const prevReviewCycle = getters.details(state)(reviewCycle);

        return api
            .post(`/api/reviewcycles/update/accessholders`, {
                reviewCycle,
                reviewee,
                accessHolders,
            })
            .then(() => {
                prevReviewCycle.accessHolders = accessHolders;

                commit("updateDetails", { reviewCycle: prevReviewCycle });
            });
    },

    saveFeedback({ commit, state }, { payload: { requestData } }) {
        const reviewCycle = getters.details(state)(reviewCycle);

        return api.post("/api/feedback/create", requestData).then(res => {
            var data = res.data;

            // set ID

            reviewCycle.feedbackSet = data.feedbackSet ? data.feedbackSet : null;

            if (data.feedbackSignals) {
                reviewCycle.feedbackSignals = data.feedbackSignals;
            }

            commit("updateDetails", { reviewCycle });
        });
    },
};

const mutations = {
    updateList(state, list) {
        state.list = list;
    },
    updateAccessDenied(state, accessDenied) {
        state.accessDenied = Boolean(accessDenied);
    },
    updateDetails(state, { reviewCycle }) {
        state.details[reviewCycle._id] = { ...reviewCycle };
    },
    updateDetailsParticipant(state, { participant }) {
        state.detailsParticipant[participant._id] = { ...participant };
    },
    updateRedirect(state, redirect) {
        state.redirect = redirect;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
